/// <amd-module name="TFS/DRITools/Components/UsageInsights/IdentityForm" />
import * as React from "react";

// VSSUI
import { Button } from "VSSUI/Button";
import { Callout } from "VSSUI/Callout";
import { IPickListItem, IPickListSelection, PickList } from "VSSUI/PickList";
import { Location } from "VSSUI/Utilities/Position";
import { IVssPersonaProps, VssPersona } from "VSSUI/VssPersona";

// VSS
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IPageData, IVssPageService } from "VSS/Platform/Page";

// DRI Tool
import { IEntity } from "TFS/DRITools/Contracts/IdentityPicker";
import * as Resources from "TFS/DRITools/Resources";
import { IdentityServiceName, IIdentityService } from "TFS/DRITools/util/IdentityService";

/**
 * IdentityForm State contains mru, resolvedValue (picked person), callout visibility
 */
export interface IIdentityFormState {
    mru: IEntity[];
    resolvedValue: IdentityRef;
    calloutVisible: boolean;
}

/**
 * Default user passed to the IdentityForm search
 */
export interface IIdentityFormProps {
    initialSearch: string;
}

const UnassignedDescriptor = "Unassigned";

const EmptyIdentityRef: IdentityRef = {
    descriptor: UnassignedDescriptor,
    id: "",
    displayName: Resources.UsageInsights_Unassigned,
    uniqueName: ""
} as IdentityRef;

export class IdentityForm extends VssComponent<IIdentityFormProps, IIdentityFormState> {
    private _identityService: IIdentityService;
    private _pickList: PickList;

    private _inputChange = {
        calloutVisible: (value: boolean): void => {
            this.setState({ calloutVisible: value });
        }
    };

    constructor(props: IIdentityFormProps, context: IVssComponentContext) {
        super(props, context);
        this._identityService = this.context.pageContext.getService<IIdentityService>(IdentityServiceName);
        this.state = {
            mru: [],
            resolvedValue: EmptyIdentityRef,
            calloutVisible: false
        };
        this._onSearch(props.initialSearch, []).then(searchResults => {
            this.setState({ resolvedValue: searchResults[0] });
        });
    }

    public componentDidMount() {
        super.componentDidMount();
        this._initializeMru();
    }

    public getIdentity(): IdentityRef {
        return this.state.resolvedValue;
    }

    public render(): JSX.Element {
        const { resolvedValue, calloutVisible } = this.state;
        return (
            <div className="picklist-callout-target identity-component">
                <div className="identity-label flex flex-center">
                    <div className="identity-label-text">{resolvedValue.displayName}</div>
                    <Button
                        subtle={true}
                        iconProps={{ iconName: "Edit" }}
                        onClick={() => this._inputChange.calloutVisible(!calloutVisible)}
                        className="edit-button"
                    />
                </div>

                {calloutVisible && (
                    <Callout
                        anchorElement={document.querySelector(".picklist-callout-target") as HTMLElement}
                        anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                        calloutOrigin={{ horizontal: Location.start, vertical: Location.start }}
                        contentShadow={true}
                        focuszoneProps={{ focusOnMount: true, includeDefaults: true }}
                        onDismiss={() => this._inputChange.calloutVisible(false)}
                    >
                        <PickList
                            componentRef={this._resolvePicklistRef}
                            width={300}
                            className="form-picklist"
                            selectedItems={[resolvedValue]}
                            items={this._getPickListItems()}
                            onSelectionChanged={this._onSelectionChanged}
                            isSearchable={true}
                            searchTextPlaceholder={Resources.UsageInsights_IdentityPicker_Placeholder}
                            minItemsForSearchBox={0}
                            getListItem={this._getListItem}
                            onRenderItemText={this._onRenderItem}
                            onSearch={this._onSearch}
                        />
                    </Callout>
                )}
            </div>
        );
    }

    private _onSelectionChanged = (selection: IPickListSelection) => {
        this._inputChange.calloutVisible(false);
        this.setState({ resolvedValue: selection.selectedItems[0] });
    };

    private _onRenderItem = (item: IPickListItem): JSX.Element => {
        const personaProps: IVssPersonaProps = {
            displayName: item.name,
            size: "small"
        };

        return (
            <div className="flex-row">
                <VssPersona {...personaProps} />
                {!!item.key && <div>item.key</div>}
            </div>
        );
    };

    private _resolvePicklistRef = (pickList: PickList) => {
        this._pickList = pickList;
        this._pickList.focus();
    };

    private _getPickListItems = (): IdentityRef[] => {
        const mru = this.state.mru.map(e => this._convertEntityToIdentityRef(e));
        return mru;
    };

    private _getListItem = (item: IdentityRef): IPickListItem => {
        return {
            name: item.displayName,
            key: item.uniqueName
        };
    };

    private _convertEntityToIdentityRef(entity: IEntity | null): IdentityRef {
        if (!entity) {
            return EmptyIdentityRef;
        }

        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData() as IPageData;
        return {
            descriptor: entity.subjectDescriptor,
            id: entity.localId || entity.originId,
            displayName: entity.displayName,
            uniqueName: entity.samAccountName ? `${entity.scopeName}\\${entity.samAccountName}` : entity.signInAddress || entity.mail,
            _links: {
                avatar: {
                    href: `${vssPageData.hostPath}/_apis/IdentityPicker/Identities/${entity.entityId}/avatar`
                }
            }
        } as IdentityRef;
    }

    private _onSearch = async (searchText: string, items: IdentityRef[]): Promise<IdentityRef[]> => {
        const searchResults = await this._identityService.searchIdentitiesAsync(searchText);
        const searchIdentities = searchResults.map(sr => this._convertEntityToIdentityRef(sr));
        return searchIdentities.filter(si => items.find(i => i.id === si.id) === undefined);
    };

    private async _initializeMru() {
        const mru = await this._identityService.getIdentityMruAsync();
        this.setState({ mru: mru });
    }
}
